import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, X } from "lucide-react";
import { useState } from "react";

/** Mirrors the API cap, so a visitor can't type more than will be kept. */
const MAX_VALUE_CHARS = 2000;

/** Up to this many variables the card starts open; longer lists start collapsed. */
const OPEN_BY_DEFAULT_UP_TO = 6;

/** Variables whose values are usually a paragraph or a list rather than a word. */
const LONG_TEXT_HINT =
  /(requirement|profile|notes?|summary|questions?|resume|description|prompt|message|instructions?|details|context)/i;

const ACRONYMS = new Set(["csv", "id", "url", "api", "ai", "jd", "hr", "cv", "ctc", "crm", "sms", "otp"]);

/** `lead_first_name` -> "Lead first name", `csv_notes` -> "CSV notes". The assistant only stores the names. */
export function humanizeVariableName(name: string): string {
  const words = name
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .map((word) => (ACRONYMS.has(word.toLowerCase()) ? word.toUpperCase() : word));
  const spaced = words.join(" ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Keep only the values the visitor actually filled in. */
export function filledVariables(values: Record<string, string>) {
  const filled: Record<string, string> = {};
  for (const [name, value] of Object.entries(values)) {
    if (value.trim()) filled[name] = value;
  }
  return filled;
}

/**
 * One field per prompt variable of the assistant, so a visitor can try a conversation with their
 * own details. Everything is optional: an empty variable stays blank in the prompt.
 *
 * Built to stay tidy with many variables: short fields sit two per row, long-form ones span the
 * row, the list scrolls inside the card with faded edges, and a long list starts collapsed so the
 * Start button stays in view.
 */
export default function VariablesForm({
  names,
  values,
  onChange,
  onClear,
}: {
  names: string[];
  values: Record<string, string>;
  onChange: (name: string, value: string) => void;
  onClear: () => void;
}) {
  const [open, setOpen] = useState(names.length <= OPEN_BY_DEFAULT_UP_TO);
  if (names.length === 0) return null;

  // Short fields first, full-width ones after: the two-column grid never leaves a gap.
  const ordered = [
    ...names.filter((name) => !LONG_TEXT_HINT.test(name)),
    ...names.filter((name) => LONG_TEXT_HINT.test(name)),
  ];
  const filled = Object.keys(filledVariables(values)).length;
  const summary =
    filled > 0 ? `${filled} of ${names.length} filled` : `${names.length} optional`;

  return (
    <div className="w-full rounded-xl border bg-card/60 text-left">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
      >
        <span>
          <span className="block text-sm font-medium">
            Details for this conversation
          </span>
          <span className="block text-xs text-muted-foreground">
            Optional. Fill in what you'd like the assistant to know.
          </span>
        </span>
        <span className="flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
          {summary}
          <ChevronDown
            className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`}
          />
        </span>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="fields"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="overflow-hidden"
          >
            <div className="border-t px-4 pb-3 pt-1">
              {/* Faded edges hint that the list scrolls; the padding keeps the first and last row clear of the fade. */}
              <div className="max-h-[44vh] overflow-y-auto py-3 pr-1 [mask-image:linear-gradient(to_bottom,transparent,black_12px,black_calc(100%-16px),transparent)]">
                <div className="grid grid-cols-1 gap-x-3 gap-y-3 sm:grid-cols-2">
                  {ordered.map((name) => {
                    const id = `var-${name}`;
                    const isLong = LONG_TEXT_HINT.test(name);
                    const common = {
                      id,
                      value: values[name] ?? "",
                      maxLength: MAX_VALUE_CHARS,
                    };
                    return (
                      <div
                        key={name}
                        className={`space-y-1 ${isLong ? "sm:col-span-2" : ""}`}
                      >
                        <Label
                          htmlFor={id}
                          className="text-xs text-muted-foreground"
                        >
                          {humanizeVariableName(name)}
                        </Label>
                        {isLong ? (
                          <Textarea
                            {...common}
                            rows={2}
                            className="min-h-[56px] resize-y"
                            onChange={(e) => onChange(name, e.target.value)}
                          />
                        ) : (
                          <Input
                            {...common}
                            onChange={(e) => onChange(name, e.target.value)}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              {filled > 0 && (
                <div className="flex justify-end pt-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 gap-1 px-2 text-xs text-muted-foreground"
                    onClick={onClear}
                  >
                    <X className="h-3 w-3" />
                    Clear all
                  </Button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
